/**
 * 行列計算ユーティリティ
 * 
 * パフォーマンスとメモリ効率を考慮し、行列を1次元配列として内部的に保持します。
 * これにより、メモリの局所性（locality）が向上し、キャッシュ効率が改善されます。
 * 
 * 行列は行優先（row-major）で格納されます：
 * [[a, b], [c, d]] は [a, b, c, d] として保存
 */

export class Matrix {
  private data: Float64Array;
  public readonly rows: number;
  public readonly cols: number;

  constructor(rows: number, cols: number, data?: number[] | Float64Array) {
    this.rows = rows;
    this.cols = cols;
    
    if (data instanceof Float64Array) {
      this.data = data;
    } else if (data) {
      this.data = new Float64Array(data);
    } else {
      this.data = new Float64Array(rows * cols);
    }
  }

  /**
   * 2次元配列から行列を作成
   */
  static from2DArray(arr: number[][]): Matrix {
    const rows = arr.length;
    const cols = arr[0].length;
    const data = new Float64Array(rows * cols);
    
    for (let i = 0; i < rows; i++) {
      for (let j = 0; j < cols; j++) {
        data[i * cols + j] = arr[i][j];
      }
    }
    
    return new Matrix(rows, cols, data);
  }

  /**
   * 行列を2次元配列に変換
   */
  to2DArray(): number[][] {
    const result: number[][] = [];
    
    for (let i = 0; i < this.rows; i++) {
      const row: number[] = [];
      for (let j = 0; j < this.cols; j++) {
        row.push(this.get(i, j));
      }
      result.push(row);
    }
    
    return result;
  }

  /**
   * 要素を取得
   */
  get(row: number, col: number): number {
    return this.data[row * this.cols + col];
  }

  /**
   * 要素を設定
   */
  set(row: number, col: number, value: number): void {
    this.data[row * this.cols + col] = value;
  }

  /**
   * 行列の転置
   */
  transpose(): Matrix {
    const result = new Matrix(this.cols, this.rows);
    
    // キャッシュ効率を考慮したブロック転置
    const blockSize = 32;
    
    for (let i = 0; i < this.rows; i += blockSize) {
      for (let j = 0; j < this.cols; j += blockSize) {
        // ブロック内の転置
        const iMax = Math.min(i + blockSize, this.rows);
        const jMax = Math.min(j + blockSize, this.cols);
        
        for (let ii = i; ii < iMax; ii++) {
          for (let jj = j; jj < jMax; jj++) {
            result.set(jj, ii, this.get(ii, jj));
          }
        }
      }
    }
    
    return result;
  }

  /**
   * 行列の積
   */
  multiply(other: Matrix): Matrix {
    if (this.cols !== other.rows) {
      throw new Error(`Matrix dimensions incompatible for multiplication: ${this.rows}x${this.cols} and ${other.rows}x${other.cols}`);
    }
    
    const result = new Matrix(this.rows, other.cols);
    
    // キャッシュ効率を考慮したブロック行列積
    const blockSize = 32;
    
    // 結果行列を0で初期化（Float64Arrayは自動的に0で初期化される）
    
    for (let i = 0; i < this.rows; i += blockSize) {
      for (let j = 0; j < other.cols; j += blockSize) {
        for (let k = 0; k < this.cols; k += blockSize) {
          // ブロック内の計算
          const iMax = Math.min(i + blockSize, this.rows);
          const jMax = Math.min(j + blockSize, other.cols);
          const kMax = Math.min(k + blockSize, this.cols);
          
          for (let ii = i; ii < iMax; ii++) {
            for (let jj = j; jj < jMax; jj++) {
              let sum = result.get(ii, jj);
              
              for (let kk = k; kk < kMax; kk++) {
                sum += this.get(ii, kk) * other.get(kk, jj);
              }
              
              result.set(ii, jj, sum);
            }
          }
        }
      }
    }
    
    return result;
  }

  /**
   * 行列の逆行列（ガウス・ジョルダン法）
   */
  inverse(): Matrix {
    if (this.rows !== this.cols) {
      throw new Error('Matrix must be square for inversion');
    }
    
    const n = this.rows;
    // 拡大行列 [A | I] を作成
    const augmented = new Matrix(n, n * 2);
    
    // 左側に元の行列、右側に単位行列をコピー
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        augmented.set(i, j, this.get(i, j));
        augmented.set(i, j + n, i === j ? 1 : 0);
      }
    }
    
    // ガウス・ジョルダン法
    for (let i = 0; i < n; i++) {
      // ピボット選択（部分ピボット選択）
      let maxRow = i;
      for (let k = i + 1; k < n; k++) {
        if (Math.abs(augmented.get(k, i)) > Math.abs(augmented.get(maxRow, i))) {
          maxRow = k;
        }
      }
      
      // 行の交換
      if (maxRow !== i) {
        for (let j = 0; j < 2 * n; j++) {
          const temp = augmented.get(i, j);
          augmented.set(i, j, augmented.get(maxRow, j));
          augmented.set(maxRow, j, temp);
        }
      }
      
      const pivot = augmented.get(i, i);
      if (Math.abs(pivot) < 1e-10) {
        throw new Error('Matrix is singular and cannot be inverted');
      }
      
      // ピボット行を正規化
      for (let j = 0; j < 2 * n; j++) {
        augmented.set(i, j, augmented.get(i, j) / pivot);
      }
      
      // 他の行を更新
      for (let k = 0; k < n; k++) {
        if (k !== i) {
          const factor = augmented.get(k, i);
          for (let j = 0; j < 2 * n; j++) {
            augmented.set(k, j, augmented.get(k, j) - factor * augmented.get(i, j));
          }
        }
      }
    }
    
    // 右側の部分（逆行列）を抽出
    const result = new Matrix(n, n);
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        result.set(i, j, augmented.get(i, j + n));
      }
    }
    
    return result;
  }

  /**
   * 行列のコピー
   */
  copy(): Matrix {
    return new Matrix(this.rows, this.cols, new Float64Array(this.data));
  }

  /**
   * 行列のノルム（フロベニウスノルム）
   */
  norm(): number {
    let sum = 0;
    for (let i = 0; i < this.data.length; i++) {
      sum += this.data[i] * this.data[i];
    }
    return Math.sqrt(sum);
  }
}

/**
 * 互換性のためのヘルパー関数
 */

export function transpose(matrix: number[][]): number[][] {
  return Matrix.from2DArray(matrix).transpose().to2DArray();
}

export function matrixMultiply(A: number[][], B: number[][]): number[][] {
  return Matrix.from2DArray(A).multiply(Matrix.from2DArray(B)).to2DArray();
}

export function inverse(matrix: number[][]): number[][] {
  return Matrix.from2DArray(matrix).inverse().to2DArray();
}